import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiStar, FiHeart, FiZap, FiTrophy } = FiIcons

const Avatar3D = ({
  character = '👦',
  isVisible = false,
  animation = 'celebrate',
  message = 'Great job!',
  pointsEarned = null, // New prop for actual points earned
  onAnimationComplete,
  size = 'large'
}) => {
  const [showSparkles, setShowSparkles] = useState(false)
  const [currentMessage, setCurrentMessage] = useState(message)
  const audioRef = useRef(null)

  const sizes = {
    small: 'w-16 h-16 text-4xl',
    medium: 'w-24 h-24 text-6xl',
    large: 'w-32 h-32 text-8xl',
    xl: 'w-40 h-40 text-9xl'
  }

  const animations = {
    celebrate: {
      scale: [0, 1.3, 1.1, 1],
      rotate: [0, -10, 10, -5, 0],
      y: [50, -20, 0],
      transition: {
        duration: 1.2,
        ease: "easeOut",
        times: [0, 0.3, 0.7, 1]
      }
    },
    bounce: {
      scale: [0, 1.2, 0.9, 1.1, 1],
      y: [0, -30, -10, -20, 0],
      transition: {
        duration: 0.8,
        ease: "easeInOut"
      }
    },
    spin: {
      scale: [0, 1],
      rotate: [0, 720],
      transition: {
        duration: 1.5,
        ease: "easeOut"
      }
    },
    pop: {
      scale: [0, 1.4, 0.8, 1.2, 1],
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    wiggle: {
      scale: [0, 1],
      rotate: [0, -15, 15, -10, 10, -5, 5, 0],
      transition: {
        duration: 1,
        ease: "easeOut"
      }
    }
  }

  const sparkleVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: [0, 1, 0],
      opacity: [0, 1, 0],
      transition: {
        duration: 1.5,
        ease: "easeOut"
      }
    }
  }

  const messageVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.8 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: 0.3,
        duration: 0.5,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      y: -10,
      scale: 0.9,
      transition: {
        duration: 0.3
      }
    }
  }

  const backgroundVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    },
    exit: {
      scale: 0.8,
      opacity: 0,
      transition: {
        duration: 0.5
      }
    }
  }

  useEffect(() => {
    if (isVisible) {
      setCurrentMessage(message)
      setShowSparkles(true)

      // Play celebration sound (optional)
      if (audioRef.current) {
        audioRef.current.volume = 0.3
        audioRef.current.play().catch(() => {
          // Audio play failed, ignore
        })
      }

      // Auto-hide after animation
      const timer = setTimeout(() => {
        setShowSparkles(false)
        if (onAnimationComplete) {
          onAnimationComplete()
        }
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [isVisible, message, onAnimationComplete])

  // Generate random sparkle positions
  const sparkles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    x: Math.random() * 200 - 100,
    y: Math.random() * 200 - 100,
    delay: Math.random() * 0.5,
    icon: [FiStar, FiHeart, FiZap][Math.floor(Math.random() * 3)]
  }))

  return (
    <>
      {/* Audio element for celebration sound */}
      <audio
        ref={audioRef}
        preload="auto"
        src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmEcBj2Y2e/GdSsFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmEcBj2Y2e/GdSsFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmEcBj2Y2e/GdSsFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmEcBj2Y2e/GdSsF"
      />

      <AnimatePresence>
        {isVisible && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={backgroundVariants}
          >
            {/* Background overlay */}
            <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" />

            {/* Sparkles */}
            {showSparkles && sparkles.map((sparkle) => (
              <motion.div
                key={sparkle.id}
                className="absolute"
                style={{
                  left: `calc(50% + ${sparkle.x}px)`,
                  top: `calc(50% + ${sparkle.y}px)`
                }}
                initial="hidden"
                animate="visible"
                variants={sparkleVariants}
                transition={{
                  ...sparkleVariants.visible.transition,
                  delay: sparkle.delay
                }}
              >
                <SafeIcon
                  icon={sparkle.icon}
                  className="text-yellow-400 text-xl drop-shadow-lg"
                />
              </motion.div>
            ))}

            {/* Main avatar container */}
            <div className="relative flex flex-col items-center">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary-400/30 via-secondary-400/30 to-accent-400/30 rounded-full blur-xl scale-150" />

              {/* Avatar character */}
              <motion.div
                className={`
                  relative ${sizes[size]} 
                  bg-gradient-to-br from-primary-100 via-white to-secondary-100 
                  rounded-full shadow-2xl flex items-center justify-center 
                  border-4 border-white backdrop-blur-sm
                `}
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { scale: 0, opacity: 0 },
                  visible: animations[animation]
                }}
                onAnimationComplete={() => {
                  // Trigger sparkle animation after main animation
                  setTimeout(() => setShowSparkles(true), 200)
                }}
              >
                <span className="drop-shadow-lg filter">
                  {character}
                </span>

                {/* Floating particles around avatar */}
                <div className="absolute inset-0">
                  {[...Array(8)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-2 h-2 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full"
                      style={{
                        left: `${50 + 40 * Math.cos((i * 2 * Math.PI) / 8)}%`,
                        top: `${50 + 40 * Math.sin((i * 2 * Math.PI) / 8)}%`
                      }}
                      animate={{
                        scale: [0, 1, 0],
                        rotate: [0, 360],
                        opacity: [0, 1, 0]
                      }}
                      transition={{
                        duration: 2,
                        delay: 0.5 + i * 0.1,
                        repeat: Infinity,
                        repeatDelay: 3
                      }}
                    />
                  ))}
                </div>
              </motion.div>

              {/* Message bubble */}
              <motion.div
                className="mt-6 px-6 py-3 bg-white rounded-full shadow-xl border-2 border-primary-200 backdrop-blur-sm"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={messageVariants}
              >
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiTrophy} className="text-yellow-500 text-lg" />
                  <span className="font-bold text-gray-800 text-lg">
                    {currentMessage}
                  </span>
                  <SafeIcon icon={FiTrophy} className="text-yellow-500 text-lg" />
                </div>

                {/* Speech bubble arrow */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-4 h-4 bg-white border-l-2 border-t-2 border-primary-200" />
              </motion.div>

              {/* Celebration text effects - Use actual points earned */}
              {pointsEarned !== null && (
                <motion.div
                  className="absolute -top-20 left-1/2 transform -translate-x-1/2"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{
                    opacity: [0, 1, 1, 0],
                    scale: [0, 1.2, 1, 0.8],
                    y: [0, -20, -30, -40]
                  }}
                  transition={{
                    duration: 2,
                    delay: 0.8,
                    times: [0, 0.3, 0.7, 1]
                  }}
                >
                  <div className="text-4xl font-bold bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent drop-shadow-lg">
                    +{pointsEarned} Point{pointsEarned !== 1 ? 's' : ''}!
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Avatar3D